import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Upload, X, Instagram } from "lucide-react";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  category: string | null;
  tags: string[] | null;
  is_published: boolean;
  author_name?: string | null;
  author_avatar_url?: string | null;
  author_instagram_url?: string | null;
  featured_on_landing?: boolean | null;
}

interface BlogPostFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingPost: BlogPost | null;
  onSuccess: () => void;
}

export function BlogPostFormModal({ open, onOpenChange, editingPost, onSuccess }: BlogPostFormModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    cover_image_url: "",
    category: "",
    author_name: "",
    author_avatar_url: "",
    author_instagram_url: "",
    tags: [] as string[],
    is_published: false,
    featured_on_landing: false,
  });
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [authorAvatarFile, setAuthorAvatarFile] = useState<File | null>(null);
  const [authorAvatarPreview, setAuthorAvatarPreview] = useState<string | null>(null);

  useEffect(() => {
    if (editingPost) {
      setFormData({
        title: editingPost.title || "",
        slug: editingPost.slug || "",
        excerpt: editingPost.excerpt || "",
        content: editingPost.content || "",
        cover_image_url: editingPost.cover_image_url || "",
        category: editingPost.category || "",
        author_name: editingPost.author_name || "",
        author_avatar_url: editingPost.author_avatar_url || "",
        author_instagram_url: editingPost.author_instagram_url || "",
        tags: editingPost.tags || [],
        is_published: editingPost.is_published || false,
        featured_on_landing: editingPost.featured_on_landing || false,
      });
      setCoverImagePreview(editingPost.cover_image_url || null);
      setAuthorAvatarPreview(editingPost.author_avatar_url || null);
    } else {
      setFormData({
        title: "",
        slug: "",
        excerpt: "",
        content: "",
        cover_image_url: "",
        category: "",
        author_name: "",
        author_avatar_url: "",
        author_instagram_url: "",
        tags: [],
        is_published: false,
        featured_on_landing: false,
      });
      setCoverImagePreview(null);
      setAuthorAvatarPreview(null);
    }
    setCoverImageFile(null);
    setAuthorAvatarFile(null);
  }, [editingPost, open]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (title: string) => {
    setFormData((prev) => ({
      ...prev,
      title,
      slug: generateSlug(title),
    }));
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imagem deve ter no máximo 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Arquivo deve ser uma imagem");
        return;
      }
      setCoverImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const removeCoverImage = () => {
    setCoverImageFile(null);
    setCoverImagePreview(null);
    setFormData((prev) => ({ ...prev, cover_image_url: "" }));
  };

  const handleAuthorAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imagem deve ter no máximo 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Arquivo deve ser uma imagem");
        return;
      }
      setAuthorAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAuthorAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const removeAuthorAvatar = () => {
    setAuthorAvatarFile(null);
    setAuthorAvatarPreview(null);
    setFormData((prev) => ({ ...prev, author_avatar_url: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Não autenticado");

      let coverImageUrl: string | null = formData.cover_image_url || null;
      if (coverImageFile) {
        const fileExt = coverImageFile.name.split(".").pop() || "jpg";
        const fileName = `${user.id}/blog-cover-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(fileName, coverImageFile, { upsert: true });
        if (uploadError) throw uploadError;
        const { data } = supabase.storage.from("avatars").getPublicUrl(fileName);
        coverImageUrl = data.publicUrl;
      }

      let authorAvatarUrl: string | null = formData.author_avatar_url || null;
      if (authorAvatarFile) {
        const fileExt = authorAvatarFile.name.split(".").pop() || "jpg";
        const fileName = `${user.id}/blog-author-avatar-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(fileName, authorAvatarFile, { upsert: true });
        if (uploadError) throw uploadError;
        const { data } = supabase.storage.from("avatars").getPublicUrl(fileName);
        authorAvatarUrl = data.publicUrl;
      }

      // Build payload, only including author fields if they have values
      // This provides backward compatibility if migration hasn't been applied yet
      const payload: any = {
        title: formData.title,
        slug: formData.slug,
        excerpt: formData.excerpt,
        content: formData.content,
        cover_image_url: coverImageUrl,
        category: formData.category,
        tags: formData.tags,
        is_published: formData.is_published,
        featured_on_landing: formData.featured_on_landing,
        author_id: user.id,
        published_at: formData.is_published ? new Date().toISOString() : null,
      };

      // Only include author fields if they have values (for backward compatibility)
      if (formData.author_name) {
        payload.author_name = formData.author_name;
      }
      if (authorAvatarUrl) {
        payload.author_avatar_url = authorAvatarUrl;
      }
      if (formData.author_instagram_url) {
        payload.author_instagram_url = formData.author_instagram_url;
      }

      // Only update when editing an existing post (has valid id from DB).
      // AI-generated posts have editingPost set but no id — treat as insert.
      const existingId = editingPost?.id;
      const isUpdate = typeof existingId === "string" && existingId.length > 0;

      if (isUpdate) {
        const { error } = await supabase
          .from("blog_posts")
          .update(payload)
          .eq("id", existingId);
        if (error) throw error;
        toast.success("Post atualizado com sucesso!");
      } else {
        const { error } = await supabase.from("blog_posts").insert(payload);
        if (error) throw error;
        toast.success("Post criado com sucesso!");
      }

      onSuccess();
    } catch (error: any) {
      console.error("Error saving blog post:", error);
      
      // Check if error is related to missing columns
      if (error?.message?.includes("author_avatar_url") || error?.message?.includes("author_name")) {
        toast.error(
          "Erro: As colunas author_name e author_avatar_url não existem no banco de dados. " +
          "Execute a migration ou o script APPLY_MIGRATION.sql no Supabase SQL Editor.",
          { duration: 8000 }
        );
      } else {
        toast.error("Erro ao salvar post: " + (error?.message || "Erro desconhecido"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingPost ? "Editar Post" : "Novo Post"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="slug">Slug (gerado automaticamente)</Label>
            <Input id="slug" value={formData.slug} disabled />
          </div>

          <div>
            <Label htmlFor="excerpt">Resumo</Label>
            <Textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData((prev) => ({ ...prev, excerpt: e.target.value }))}
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="category">Categoria</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
              placeholder="Ex: Saúde Digital, Dicas, Novidades"
            />
          </div>

          <div>
            <Label htmlFor="author_name">Autor(a)</Label>
            <Input
              id="author_name"
              value={formData.author_name}
              onChange={(e) => setFormData((prev) => ({ ...prev, author_name: e.target.value }))}
              placeholder="Nome do autor do post"
            />
          </div>

          <div>
            <Label>Avatar do Autor</Label>
            <div className="flex items-center gap-4 mt-2">
              {authorAvatarPreview || formData.author_avatar_url ? (
                <div className="relative">
                  <img
                    src={authorAvatarPreview || formData.author_avatar_url || ""}
                    alt="Avatar do autor"
                    className="w-24 h-24 rounded-full object-cover border-2 border-border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-1 -right-1 h-6 w-6 rounded-full"
                    onClick={removeAuthorAvatar}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <label className="w-24 h-24 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center cursor-pointer hover:border-primary transition-colors">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAuthorAvatarChange}
                    className="hidden"
                  />
                </label>
              )}
              {!(authorAvatarPreview || formData.author_avatar_url) && (
                <span className="text-sm text-muted-foreground">
                  Clique para enviar foto (máx. 5MB)
                </span>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="author_instagram_url">Instagram do Autor</Label>
            <div className="relative mt-1">
              <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="author_instagram_url"
                value={formData.author_instagram_url}
                onChange={(e) => setFormData((prev) => ({ ...prev, author_instagram_url: e.target.value }))}
                placeholder="https://instagram.com/usuario"
                className="pl-9"
              />
            </div>
          </div>

          <div>
            <Label>Envio Imagem Capa</Label>
            <div className="flex items-center gap-4 mt-2">
              {coverImagePreview || formData.cover_image_url ? (
                <div className="relative">
                  <img
                    src={coverImagePreview || formData.cover_image_url || ""}
                    alt="Capa"
                    className="w-32 h-32 object-cover rounded border-2 border-border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-1 -right-1 h-6 w-6 rounded-full"
                    onClick={removeCoverImage}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <label className="w-32 h-32 border-2 border-dashed border-muted-foreground flex items-center justify-center cursor-pointer hover:border-primary transition-colors rounded">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="hidden"
                  />
                </label>
              )}
              {!(coverImagePreview || formData.cover_image_url) && (
                <span className="text-sm text-muted-foreground">
                  Clique para enviar imagem (máx. 5MB)
                </span>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="content">Conteúdo (Markdown) *</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
              rows={15}
              required
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <Label htmlFor="is_published">Publicado</Label>
            <Switch
              id="is_published"
              checked={formData.is_published}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_published: checked }))
              }
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label htmlFor="featured_on_landing">Exibir na Landing Page</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Marque para exibir este post na página inicial
              </p>
            </div>
            <Switch
              id="featured_on_landing"
              checked={formData.featured_on_landing}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, featured_on_landing: checked }))
              }
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
